import { useMemo } from 'react';

import { Modal, Spinner, Text, toastService } from '@thefarmersfront/kitchen';

import { ModalContentsContainer, ModalSection, Table } from '~/components/common';

import { useGetReturnDisposalIssueItemsQuery, usePutReturnDisposalScrapMutation } from '~/hooks/receive';
import { formatNumber, getReturnDisposalScrapItemsColumns, getScrapFee, getTotalScrapWeightKg } from '~/utils';

import { RETURN_DISPOSAL_REQUEST_MESSAGE, SCRAP_NOTICE, TOAST_MESSAGE } from '~/constants';

import type { TypeReturnDisposalIssueItemApi } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalScrapModal.module.scss';

type TypeReceiveReturnDisposalScrapModalProps = {
  isOpen: boolean;
  inspectionIds: number[];
  onClose: () => void;
  /** 신청 성공 시(부모에서 목록 재조회 + 모달 닫기) */
  onSuccess: () => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 폐기 처리 신청 모달
 *
 * @note 폐기 비용 안내 + 상품정보 미리보기(이슈발생센터·중량 포함)만 노출하며 별도 입력 없이 신청한다.
 *       총 폐기중량·예상 비용은 선택 이슈카드의 카드별 폐기중량(weightKg) 합으로 계산해 안내 문구에 치환한다.
 */
const ReceiveReturnDisposalScrapModal = ({
  isOpen,
  inspectionIds,
  onClose,
  onSuccess,
}: TypeReceiveReturnDisposalScrapModalProps) => {
  const issueItemsQuery = useGetReturnDisposalIssueItemsQuery({ inspectionIds, enabled: isOpen });
  const submitMutation = usePutReturnDisposalScrapMutation();

  const issueItems = useMemo(() => issueItemsQuery.data ?? [], [issueItemsQuery.data]);

  const columns = useMemo(() => getReturnDisposalScrapItemsColumns(), []);

  /** 폐기 비용 안내 문구(총 폐기중량·예상 비용 치환, 중량 소수 둘째 자리 고정) */
  const noticeEstimate = useMemo(() => {
    const totalWeight = getTotalScrapWeightKg(issueItems);
    const weightText = formatNumber(totalWeight, '', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return SCRAP_NOTICE.ESTIMATE.replace(/\{weight\}/g, weightText).replace(
      '{fee}',
      formatNumber(getScrapFee(totalWeight))
    );
  }, [issueItems]);

  const isValid = issueItems.length > 0 && !submitMutation.isPending;

  const handleSubmit = () => {
    submitMutation.mutate(
      { inspectionIds },
      {
        onSuccess: () => {
          toastService.addToast({ message: RETURN_DISPOSAL_REQUEST_MESSAGE.SCRAP_SUBMIT_SUCCESS });
          onSuccess();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  return (
    <Modal
      title="폐기 처리신청"
      size="xlarge"
      open={isOpen}
      confirmText="신청하기"
      disabled={!isValid}
      onCancel={onClose}
      onConfirm={handleSubmit}
    >
      {issueItemsQuery.isLoading ? (
        <div className={styles['state-area']}>
          <Spinner />
        </div>
      ) : issueItemsQuery.isError ? (
        <div className={styles['state-area']}>
          <Text size="large" color="tertiary">
            정보를 불러오지 못했습니다.
          </Text>
        </div>
      ) : (
        <ModalContentsContainer className={styles['modal-body']}>
          <div className={styles['notice-box']}>
            <span className={styles['notice-title']}>{SCRAP_NOTICE.TITLE}</span>
            <ul className={styles['notice-list']}>
              <li>{SCRAP_NOTICE.CHARGE}</li>
              <li>{noticeEstimate}</li>
            </ul>
          </div>

          <ModalSection title="상품 정보">
            <Table<TypeReturnDisposalIssueItemApi>
              className={styles['preview-table']}
              data={issueItems}
              columns={columns}
              rowHeight={40}
              maxHeight="220px"
              loading={issueItemsQuery.isLoading}
              error={issueItemsQuery.isError}
            />
          </ModalSection>
        </ModalContentsContainer>
      )}
    </Modal>
  );
};

export default ReceiveReturnDisposalScrapModal;
